import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLayout from "./components/SiteLayout";
import Home from "./pages/Home";
import { applyRouteHead } from "./pageMeta";
import { prefetchCareers } from "./prefetch";

// Route-level code splitting (spec d, WP-C). Careers + JobDetail share one
// chunk (and the Firestore chunk); the admin chunk carries Auth, the CRM,
// admin.css and the admin strings, and resolves after its strings register.
const AdminApp = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./AdminApp").then((m) =>
    m.adminReady.then(() => m)
  )
);
// Hard load of a careers URL: fetch Firestore + the job data in parallel with
// the lazy route chunk instead of after it (no-op on every other path).
prefetchCareers(window.location.pathname);

const Careers = lazy(() => import(/* webpackChunkName: "careers" */ "./pages/Careers"));
const JobDetail = lazy(() => import(/* webpackChunkName: "careers" */ "./pages/JobDetail"));
const Legal = lazy(() => import(/* webpackChunkName: "legal" */ "./pages/Legal"));
const NotFound = lazy(() => import(/* webpackChunkName: "legal" */ "./pages/NotFound"));

/**
 * Empty, viewport-tall placeholder while a route chunk loads: renders nothing
 * visible but keeps the footer from jumping (CLS) on a hard load of a lazy route.
 */
const RoutePending: React.FC = () => <div aria-hidden="true" style={{ minHeight: "100vh" }} />;

/** Default title/description/canonical per route and language; scroll to top on SPA navigation. */
const HeadSync: React.FC = () => {
  const { pathname, hash } = useLocation();
  const { t, i18n } = useTranslation();
  const first = useRef(true);

  useEffect(() => {
    applyRouteHead(pathname, t("meta.title"), t("meta.description"));
  }, [pathname, t, i18n.language]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

const PublicLayout: React.FC = () => (
  <SiteLayout>
    <Suspense fallback={<RoutePending />}>
      <Outlet />
    </Suspense>
  </SiteLayout>
);

const App: React.FC = () => (
  <BrowserRouter>
    <HeadSync />
    <Routes>
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={null}>
            <AdminApp />
          </Suspense>
        }
      />
      <Route element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="careers" element={<Careers />} />
        <Route path="careers/:id" element={<JobDetail />} />
        <Route path="privacy" element={<Legal kind="privacy" />} />
        <Route path="terms" element={<Legal kind="terms" />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  </BrowserRouter>
);

export default App;
